import type { MeasurementUnit } from '@/models/Filter';
import { CHANGE_TYPE, CONVERSIONS, ROOM_LAYOUT } from '@/constants/capacityCalculatorConstants';
import { queryStringConstants } from '@/constants/queryConstants';

type ConvertedSpaceValue = {
  IMPERIAL: number;
  METRIC: number;
};

function tweakRoomFactorForSocialDistancing(includeSocialDistancing, socialDistance, roomTypeFactor, measurementUnit) {
  let socialDistanceArea = socialDistance ** 2;
  /*
   * the room factor is always assumed to have the following unit:
   * attendees per square foot
   * thus if the social distance input is in meters, we need to convert it
   * by the time it makes its way back out to the UI it will be in metric again,
   * it's just that for the sake of the intermediate calculation it needs to be imperial
   */
  if (measurementUnit === queryStringConstants.METRIC) {
    socialDistanceArea *= CONVERSIONS.METRIC_TO_IMPERIAL_CONVERSION_VALUE;
  }
  if (includeSocialDistancing && socialDistanceArea > 1 / roomTypeFactor) {
    return 1 / socialDistanceArea;
  }

  return roomTypeFactor;
}

function getRoomTypeFactor(
  roomSpaceValue: number,
  attendeesValue: number,
  changeType: string,
  measurementUnit: MeasurementUnit,
  roomLayoutValue: string,
  errorLabels: Record<string, string>
) {
  let failureMessage = '';
  let roomTypeFactor = 0;
  switch (roomLayoutValue) {
    case ROOM_LAYOUT.BANQUET_60:
    case ROOM_LAYOUT.BANQUET_72:
      roomTypeFactor = 0.0832;
      break;
    case ROOM_LAYOUT.CONFERENCE:
      // different validation message based on which textbox was updated
      if (changeType === CHANGE_TYPE.CHANGE_ROOM_SPACE_VALUE) {
        if (
          roomSpaceValue > CONVERSIONS.ROOM_SPACE_CONFERENCE_IMPERIAL_MAXIMUM &&
          measurementUnit === queryStringConstants.IMPERIAL
        ) {
          failureMessage = errorLabels.roomSpaceConferenceStandard;
        } else if (
          roomSpaceValue > CONVERSIONS.ROOM_SPACE_CONFERENCE_METRIC_MAXIMUM &&
          measurementUnit === queryStringConstants.METRIC
        ) {
          failureMessage = errorLabels.roomSpaceConferenceMetric;
        }
      } else if (attendeesValue > CONVERSIONS.ATTENDEES_CONFERENCE_MAXIMUM) {
        failureMessage = errorLabels.attendeesConference;
      }
      roomTypeFactor = 0.024; // Conference
      break;
    case ROOM_LAYOUT.H_SQUARE:
      // different validation message based on which textbox was updated
      if (changeType === CHANGE_TYPE.CHANGE_ROOM_SPACE_VALUE) {
        if (
          (roomSpaceValue > CONVERSIONS.ROOM_SPACE_H_SQUARE_IMPERIAL_MAXIMUM ||
            roomSpaceValue < CONVERSIONS.ROOM_SPACE_H_SQUARE_IMPERIAL_MINIMUM) &&
          measurementUnit === queryStringConstants.IMPERIAL
        ) {
          failureMessage = errorLabels.roomSpaceHSquareStandard;
        } else if (
          (roomSpaceValue > CONVERSIONS.ROOM_SPACE_H_SQUARE_METRIC_MAXIMUM ||
            roomSpaceValue < CONVERSIONS.ROOM_SPACE_H_SQUARE_METRIC_MINIMUM) &&
          measurementUnit === queryStringConstants.METRIC
        ) {
          failureMessage = errorLabels.roomSpaceHSquareMetric;
        }
      } else if (
        attendeesValue > CONVERSIONS.ATTENDEES_H_SQUARE_MAXIMUM ||
        attendeesValue < CONVERSIONS.ATTENDEES_H_SQUARE_MINIMUM
      ) {
        failureMessage = errorLabels.attendeesHSquare;
      }
      roomTypeFactor = 0.024; // H-Square
      break;
    case ROOM_LAYOUT.RECEPTION:
      roomTypeFactor = 0.1; // Reception
      break;
    case ROOM_LAYOUT.SCHOOL_ROOM_18:
      roomTypeFactor = 0.069; // School Room - 18"
      break;
    case ROOM_LAYOUT.SCHOOL_ROOM_30:
      roomTypeFactor = 0.057; // School Room - 30"
      break;
    case ROOM_LAYOUT.TABLET_CHAIR:
      roomTypeFactor = 0.1; // tabletChair
      break;
    case ROOM_LAYOUT.THEATER:
      roomTypeFactor = 0.111; // Theatre
      break;
    case ROOM_LAYOUT.TRADE_SHOW_8_10:
      if (changeType === CHANGE_TYPE.CHANGE_ROOM_SPACE_VALUE) {
        if (
          roomSpaceValue < CONVERSIONS.ROOM_SPACE_TRADE_SHOW_IMPERIAL_MAXIMUM &&
          measurementUnit === queryStringConstants.IMPERIAL
        ) {
          failureMessage = errorLabels.roomSpaceTradeShowStandard;
        } else if (
          roomSpaceValue < CONVERSIONS.ROOM_SPACE_TRADE_SHOW_METRIC_MAXIMUM &&
          measurementUnit === queryStringConstants.METRIC
        ) {
          failureMessage = errorLabels._room_layout_validation_message_room_space_trade_show_metric;
        }
      }
      roomTypeFactor = 0.00625; // trade Show 8 x 10
      break;
    case ROOM_LAYOUT.TRADE_SHOW_10_X_10:
      if (changeType === CHANGE_TYPE.CHANGE_ROOM_SPACE_VALUE) {
        if (
          roomSpaceValue < CONVERSIONS.ROOM_SPACE_TRADE_SHOW_IMPERIAL_MAXIMUM &&
          measurementUnit === queryStringConstants.IMPERIAL
        ) {
          failureMessage = errorLabels.roomSpaceTradeShowStandard;
        } else if (
          roomSpaceValue < CONVERSIONS.ROOM_SPACE_TRADE_SHOW_METRIC_MAXIMUM &&
          measurementUnit === queryStringConstants.METRIC
        ) {
          failureMessage = errorLabels.roomSpaceTradeShowMetric;
        }
      }
      roomTypeFactor = 0.005; // trade Show 10 x 10
      break;
    case ROOM_LAYOUT.U_SHAPE:
      roomTypeFactor = 0.0286; // U-shape
      break;
    case ROOM_LAYOUT.CRESCENT_ROUNDS_60:
    case ROOM_LAYOUT.CRESCENT_ROUNDS_66:
    case ROOM_LAYOUT.CRESCENT_ROUNDS_72:
      roomTypeFactor = 0.0593;
      break;
    default:
      roomTypeFactor = 0;
  }
  return { roomTypeFactor, failureMessage };
}

export function calculateAttendees(roomSpaceValue, measurementUnit, roomTypeFactor, rearProjectionRequired) {
  let attendeesValue = roomSpaceValue * roomTypeFactor;
  if (measurementUnit === queryStringConstants.METRIC) {
    attendeesValue *= CONVERSIONS.METRIC_TO_IMPERIAL_CONVERSION_VALUE;
  }
  // apply rear projection scaling
  if (rearProjectionRequired) {
    attendeesValue -= attendeesValue * CONVERSIONS.REAR_PROJECTION_SCALING_FACTOR;
  }
  return Math.round(attendeesValue);
}

// conversion for room space value based on units
function calculateConvertedSpaceValue(value: number, measurementUnit: MeasurementUnit): ConvertedSpaceValue {
  let IMPERIAL: number;
  let METRIC: number;
  if (measurementUnit === queryStringConstants.IMPERIAL) {
    IMPERIAL = value;
    METRIC = value / CONVERSIONS.METRIC_TO_IMPERIAL_CONVERSION_VALUE;
  } else {
    METRIC = value;
    IMPERIAL = value * CONVERSIONS.METRIC_TO_IMPERIAL_CONVERSION_VALUE;
  }
  return {
    IMPERIAL,
    METRIC
  };
}

/**
 * calculate the room space based on the number of attendees, room type factor and rear projection
 */
export function calculateRoomSpaceValue(
  attendeesCount: number,
  measurementUnit: MeasurementUnit,
  roomTypeFactor: number,
  rearProjectionRequired: boolean
): ConvertedSpaceValue {
  let roomSpace = attendeesCount / roomTypeFactor;
  if (measurementUnit === queryStringConstants.METRIC) {
    roomSpace /= CONVERSIONS.METRIC_TO_IMPERIAL_CONVERSION_VALUE;
  }
  const roomSpaceValues = calculateConvertedSpaceValue(roomSpace, measurementUnit);
  // apply rear projection scaling
  if (rearProjectionRequired) {
    roomSpaceValues.IMPERIAL /= 1 - CONVERSIONS.REAR_PROJECTION_SCALING_FACTOR;
    roomSpaceValues.METRIC /= 1 - CONVERSIONS.REAR_PROJECTION_SCALING_FACTOR;
  }
  return roomSpaceValues;
}

/**
 * Calculate the largest room space based on the number of attendees or attendees based on largest room
 */

export function calculateOutput(
  roomSpaceValue: number,
  attendeesCount: number,
  changeType: string,
  measurementUnit: MeasurementUnit,
  roomLayoutValue: string,
  requireRearProjection: boolean,
  includeSocialDistancing: boolean,
  socialDistance: number,
  errorLabels: Record<string, string>
) {
  // calculate roomTypeFactor
  const { roomTypeFactor, failureMessage } = getRoomTypeFactor(
    roomSpaceValue,
    attendeesCount,
    changeType,
    measurementUnit,
    roomLayoutValue,
    errorLabels
  );
  let output;
  output = changeType === CHANGE_TYPE.CHANGE_ROOM_SPACE_VALUE ? attendeesCount : roomSpaceValue;

  if (failureMessage.length) {
    return { output, failureMessage };
  }

  // Update roomTypeFactor based on social distance
  const actualRoomTypeFactor = tweakRoomFactorForSocialDistancing(
    includeSocialDistancing,
    socialDistance,
    roomTypeFactor,
    measurementUnit
  );

  if (changeType === CHANGE_TYPE.CHANGE_ROOM_SPACE_VALUE) {
    // calculate final attendees
    output = calculateAttendees(roomSpaceValue, measurementUnit, actualRoomTypeFactor, requireRearProjection);

    return { output, failureMessage };
  }
  // calculate final roomSpace
  output = calculateRoomSpaceValue(attendeesCount, measurementUnit, actualRoomTypeFactor, requireRearProjection)[
    measurementUnit
  ];

  output = Math.round(output);

  return { output, failureMessage };
}
